import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "./hooks/use-auth";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";

import ChatAssistant from "./components/ChatAssistant";
import CookieConsent from "./components/CookieConsent";
import CartDrawer from "./components/CartDrawer";
import ErrorBoundary from "./components/ErrorBoundary";
import ShopPauseDialog from "./components/ShopPauseDialog";

// Code-split heavier / less-frequent routes
const ShopPage = lazy(() => import("./pages/ShopPage"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const ImpressumPage = lazy(() => import("./pages/ImpressumPage"));
const DatenschutzPage = lazy(() => import("./pages/DatenschutzPage"));
const WiderrufsbelehrungPage = lazy(() => import("./pages/WiderrufsbelehrungPage"));
const AGBPage = lazy(() => import("./pages/AGBPage"));
const VersandPage = lazy(() => import("./pages/VersandPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const RouteFallback = () => (
  <div
    role="status"
    aria-live="polite"
    aria-label="Seite wird geladen"
    className="min-h-[60vh] flex items-center justify-center"
  >
    <div className="h-10 w-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
  </div>
);

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <a href="#main-content" className="skip-link">Zum Inhalt springen</a>
          <ScrollToTop />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/produkt/:id" element={<ProductDetail />} />
              <Route path="/ueber-uns" element={<AboutPage />} />
              <Route path="/galerie" element={<GalleryPage />} />
              <Route path="/warenkorb" element={<CartPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/impressum" element={<ImpressumPage />} />
              <Route path="/datenschutz" element={<DatenschutzPage />} />
              <Route path="/widerrufsbelehrung" element={<WiderrufsbelehrungPage />} />
              <Route path="/agb" element={<AGBPage />} />
              <Route path="/zahlung-versand" element={<VersandPage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <ChatAssistant />
          <CartDrawer />
          <CookieConsent />
          <ShopPauseDialog />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
